import type { ReactElement } from 'react';

import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import Typography from '@mui/material/Typography';

import { checkIfIsArray, invariant } from '@e-socna/utils';
import { EMPTY_ARRAY } from '@e-socna/empty';
import { RESOLVER_CONTENT_CLASSNAME } from '@e-socna/printer-contract';

import { useDocumentResolverContext } from '../contexts/DocumentResolverContext/hooks/useDocumentResolverContext.hook';
import type {
  ResolverParameters,
  ResolverResult,
} from '../types/resolvers.type';

export const useDocTableDesMatieres = ({
  enabled,
}: Readonly<ResolverParameters>): ResolverResult<ReactElement> => {
  const { printerSettings } = useDocumentResolverContext();

  if (!enabled) {
    return {
      data: undefined,
      error: null,
      isLoading: false,
      isSuccess: false,
    };
  }

  if (!printerSettings) {
    return {
      // eslint-disable-next-line react/jsx-no-useless-fragment
      data: { generated: <></> },
      error: null,
      isLoading: false,
      isSuccess: true,
    };
  }

  const sectionTitles = JSON.parse(
    printerSettings.sectionTitlesList,
  ) as unknown;

  invariant(checkIfIsArray(sectionTitles), 'sectionTitles is not an array');

  const sectionDisplayFirstPageNumbers =
    printerSettings.sectionDisplayFirstPageNumbers
      ? (JSON.parse(
          printerSettings.sectionDisplayFirstPageNumbers,
        ) as readonly string[])
      : EMPTY_ARRAY;

  const generated = (
    <div className={RESOLVER_CONTENT_CLASSNAME}>
      <Typography variant="h2">Table des matières</Typography>
      <List
        sx={{
          listStyleType: 'disc',
          pl: 2,
        }}
      >
        {sectionTitles.map((sectionTitle, index) => {
          invariant(
            typeof sectionTitle === 'string',
            'sectionTitle is not a string',
          );

          return (
            <ListItem
              // eslint-disable-next-line react/no-array-index-key
              key={index}
              sx={{
                display: 'list-item',
              }}
            >
              <span>{sectionTitle}</span>
              {!!sectionDisplayFirstPageNumbers[index] && (
                <span style={{ float: 'right' }}>
                  p. {sectionDisplayFirstPageNumbers[index]}
                </span>
              )}
            </ListItem>
          );
        })}
      </List>
    </div>
  );

  return {
    data: { generated },
    error: null,
    isLoading: false,
    isSuccess: true,
  };
};
